export interface BuilderError {
  raw: string;
  pageIndex?: number;
  /**
   * Top-level element index, populated only for flat (non-nested) errors. Kept
   * for back-compat with the existing inline-error filter; nested element
   * errors identify themselves via ``elementId`` instead.
   */
  elementIndex?: number;
  /**
   * Stable element identifier resolved by walking the parsed backend path
   * against the current draft. Lets the property pane match an error to an
   * element regardless of where it lives (page root, ``field_group.children``,
   * ``columns.columns[*].children``). Also populated by client-side
   * preflight (``validateDraft``) so the same inline-error renderer covers
   * both pre- and post-save sources.
   */
  elementId?: string;
  /** Option slot index for choice errors (``options[N].value/label.*``). */
  optionIndex?: number;
  field?: string;
}

/**
 * Captures ``pages[P]`` plus an optional element path that may include
 * ``elements[N]``, ``children[N]``, and ``columns[N]`` segments — the same
 * shape ``cognota.intake.schema._validate_elements`` emits when walking
 * containers. Trailing capture is the field/options descriptor (``label.en``,
 * ``options[0].value`` …).
 */
const PATH_RE =
  /^pages\[(\d+)\]((?:\.(?:elements|children|columns)\[\d+\])*)\.([a-zA-Z0-9_.[\]]+)\b/;
const SEGMENT_RE = /\.(elements|children|columns)\[(\d+)\]/g;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function pushStrings(target: string[], value: unknown): void {
  if (typeof value === "string") {
    target.push(value);
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) pushStrings(target, item);
    return;
  }
  if (isRecord(value)) {
    for (const item of Object.values(value)) pushStrings(target, item);
  }
}

function extractMessages(err: unknown): string[] {
  const messages: string[] = [];
  const root: unknown =
    isRecord(err) && "response" in err && isRecord((err as { response?: unknown }).response)
      ? ((err as { response: { data?: unknown } }).response.data ?? err)
      : err;

  if (Array.isArray(root)) {
    pushStrings(messages, root);
  } else if (isRecord(root)) {
    if ("definition" in root) {
      pushStrings(messages, root.definition);
    } else if ("detail" in root && typeof root.detail === "string") {
      messages.push(root.detail);
    } else {
      pushStrings(messages, root);
    }
  } else if (typeof root === "string") {
    messages.push(root);
  }

  if (messages.length === 0 && err instanceof Error) {
    messages.push(err.message);
  }
  return messages;
}

interface PathSegment {
  list: "elements" | "children" | "columns";
  index: number;
}

function parsePathSegments(raw: string): PathSegment[] {
  const segments: PathSegment[] = [];
  let match: RegExpExecArray | null;
  SEGMENT_RE.lastIndex = 0;
  while ((match = SEGMENT_RE.exec(raw)) !== null) {
    segments.push({
      list: match[1] as PathSegment["list"],
      index: Number(match[2]),
    });
  }
  return segments;
}

interface DefinitionLike {
  pages: { elements: unknown[] }[];
}

/**
 * Walk the path segments through ``definition`` and return the leaf element's
 * id, when resolvable. Used to bind a backend-emitted error to the element row
 * in the builder even when it lives inside a ``field_group`` / ``columns``.
 */
function resolveElementId(
  definition: DefinitionLike | null | undefined,
  pageIndex: number,
  segments: PathSegment[],
): string | undefined {
  if (!definition) return undefined;
  const page = definition.pages?.[pageIndex];
  if (!page) return undefined;
  let cursor: unknown = page;
  for (const segment of segments) {
    if (!cursor || typeof cursor !== "object") return undefined;
    const record = cursor as Record<string, unknown>;
    if (segment.list === "columns") {
      const columns = record.columns;
      if (!Array.isArray(columns)) return undefined;
      cursor = columns[segment.index];
      continue;
    }
    const list = record[segment.list];
    if (!Array.isArray(list)) return undefined;
    cursor = list[segment.index];
  }
  if (cursor && typeof cursor === "object") {
    const id = (cursor as { id?: unknown }).id;
    if (typeof id === "string") return id;
  }
  return undefined;
}

const OPTION_INDEX_RE = /^options\[(\d+)\]/;

export function parseSaveErrors(
  err: unknown,
  definition?: DefinitionLike | null,
): { inline: BuilderError[]; banner: string[] } {
  const messages = extractMessages(err);
  const inline: BuilderError[] = [];
  const banner: string[] = [];
  for (const raw of messages) {
    const match = PATH_RE.exec(raw);
    if (match) {
      const pageIndex = Number(match[1]);
      const tail = match[2] ?? "";
      const field = match[3];
      const segments = parsePathSegments(tail);

      // Flat-top-level back-compat: pages[N].elements[M].<field>
      const flatElementIndex =
        segments.length === 1 && segments[0]?.list === "elements" ? segments[0].index : undefined;
      const elementId = resolveElementId(definition, pageIndex, segments);
      const optionMatch = field ? OPTION_INDEX_RE.exec(field) : null;
      inline.push({
        raw,
        pageIndex,
        elementIndex: flatElementIndex,
        elementId,
        optionIndex: optionMatch ? Number(optionMatch[1]) : undefined,
        field,
      });
    } else {
      banner.push(raw);
    }
  }
  return { inline, banner };
}
